import React, { useState, useEffect, useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import { useTheme } from '../App';
import { useT } from '../lib/i18n';
import { normalizeRichText } from '../lib/richText';
import { toLeftPct, toTopPct, effectStyle as heroEffectStyle, HERO_KEYFRAMES, heroTitleSize } from '../lib/heroCoords';
import { isAurexFamily } from '../lib/themeColors';
import { useAuth } from '../lib/auth';
import { resolveCta } from '../lib/ctaActions';

function resolveVideoEmbed(url) {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?\s]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  if (url.startsWith('<iframe')) {
    const srcMatch = url.match(/src=["']([^"']+)["']/);
    if (srcMatch) return srcMatch[1];
  }
  return url;
}

function VideoEmbed({ url, style, className }) {
  const embedUrl = resolveVideoEmbed(url);
  if (!embedUrl) return null;
  return (
    <div className={className} style={style}>
      <iframe src={embedUrl} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen frameBorder="0" title="Hero Video" />
    </div>
  );
}

// Renders up to 3 CTAs as an inline pill row. First is the solid primary,
// rest are outlined secondary. Order/visibility is admin-controlled via the
// respective button_text fields on the slide.
function HeroButtonsRow({ slide, size = 'md', dataTestId }) {
  const tt = useT();
  const { user } = useAuth();
  const loggedIn = !!user;
  // Each button declares an Action (button{N}_action) resolved by lib/ctaActions:
  // session-based visibility gate + action→href. Legacy #login/#waiting-list URLs
  // are inferred, so existing heroes keep working untouched. See PBHero (parity).
  const buttons = [
    { text: slide.button_text,   url: tt(slide.button_url || slide.button_link), target: slide.window_open === 'new' ? '_blank' : '_self',          action: slide.button_action },
    { text: slide.button_2_text, url: tt(slide.button_2_url), target: slide.button_2_window_open === 'new' ? '_blank' : '_self', action: slide.button_2_action },
    { text: slide.button_3_text, url: tt(slide.button_3_url), target: slide.button_3_window_open === 'new' ? '_blank' : '_self', action: slide.button_3_action },
  ]
    .filter(b => tt(b.text))
    .map(b => { const r = resolveCta(b, { loggedIn }); return r.visible ? { ...b, href: r.href, target: r.target } : null; })
    .filter(Boolean);
  if (buttons.length === 0) return null;
  const padding = size === 'sm' ? 'px-6 py-2.5' : 'px-8 py-3';
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid={dataTestId || 'hero-cta-row'}>
      {buttons.map((b, i) => {
        const primary = i === 0;
        return (
          <a key={i} href={b.href || '#'} target={b.target} rel="noopener noreferrer"
            className={`inline-flex items-center gap-2 ${padding} rounded-sm font-medium transition-all text-sm hover:opacity-90 ${primary ? 'bg-white' : 'bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#1a2332]'}`}
            style={primary ? { color: 'var(--color-primary, #1a2332)' } : undefined}
            data-testid={`hero-cta-btn-${i}`}
          >
            {tt(b.text)} {primary && <ArrowRight className="w-4 h-4" />}
          </a>
        );
      })}
    </div>
  );
}

export default function HeroSection({ data, slides }) {
  const theme = useTheme();
  const tt = useT();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const allSlides = useMemo(() => slides && slides.length > 0 ? slides : (data?.title ? [data] : []), [slides, data]);

  useEffect(() => {
    if (allSlides.length <= 1) return;
    const s = allSlides[currentSlide];
    const delay = s?.delay || 9400;
    const timer = setTimeout(() => {
      setCurrentSlide(prev => (prev + 1) % allSlides.length);
      setAnimKey(prev => prev + 1);
    }, delay);
    return () => clearTimeout(timer);
  }, [currentSlide, allSlides]);

  if (allSlides.length === 0) return null;
  const slide = allSlides[currentSlide];
  const isLegacy = !slide.slide_type;
  const bg = slide.background || slide.background_image || '';
  const speed = slide.speed_per_layer || 400;

  const effectStyle = (effect, startDelay) => heroEffectStyle(effect, startDelay, speed);

  const isModernLike = theme === 'modern' || isAurexFamily(theme);
  const heroClasses = isModernLike
    ? 'relative min-h-[750px] md:min-h-[800px] flex items-center overflow-hidden'
    : theme === 'classic'
    ? 'relative min-h-[500px] md:min-h-[550px] flex items-center overflow-hidden'
    : 'relative min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden';

  const overlayStyle = isModernLike
    ? { background: `linear-gradient(135deg, rgba(15,23,42,0.85), rgba(30,41,59,0.7))` }
    : theme === 'classic'
    ? { background: `linear-gradient(to right, var(--color-primary, #1a2332)f0, var(--color-primary, #1a2332)80)` }
    : { background: `linear-gradient(to right, var(--color-primary, #1a2332)ee, var(--color-primary, #1a2332)99)` };

  return (
    <section className={heroClasses} data-testid="hero-section">
      <style>{HERO_KEYFRAMES}</style>
      <div className="absolute inset-0 bg-cover bg-center transition-all duration-700" style={{ backgroundImage: bg ? `url(${bg})` : 'none' }} />
      <div className="absolute inset-0" style={overlayStyle} />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 w-full" key={animKey}>
        {isLegacy ? (
          <>
            <p className="text-xs uppercase tracking-[0.3em] font-semibold mb-4" style={{ color: 'var(--color-accent, #0D9488)' }} data-testid="hero-subtitle">{tt(slide.subtitle)}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-2xl" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="hero-title">
              {tt(slide.title)?.split('\n').map((line, i) => <React.Fragment key={i}>{i > 0 && <br />}<span className={i > 0 ? 'italic' : ''}>{line}</span></React.Fragment>)}
            </h1>
            <p className="text-white/70 mt-6 max-w-xl text-base md:text-lg leading-relaxed" data-testid="hero-description">{tt(slide.description)}</p>
            <div className="mt-8"><HeroButtonsRow slide={slide} /></div>
          </>
        ) : (
          <>
            {/* Desktop (lg+): Absolute positioning from CMS coordinates */}
            <div className="relative w-full hidden lg:block" style={{ minHeight: '400px' }}>
              {slide.title && (
                <div className="absolute max-w-[55%]" style={{ left: toLeftPct(slide.title_x, 100), top: toTopPct(slide.title_y, 50), ...effectStyle(slide.title_effect, slide.title_start) }} data-testid="hero-title">
                  <div className="text-5xl xl:text-6xl font-bold leading-tight [&_em]:italic" style={{ fontFamily: 'Playfair Display, serif', color: 'white' }} dangerouslySetInnerHTML={{ __html: normalizeRichText(tt(slide.title)) }} />
                </div>
              )}
              {slide.subtitle && (
                <div className="absolute max-w-[55%]" style={{ left: toLeftPct(slide.subtitle_x, 100), top: toTopPct(slide.subtitle_y, 80), ...effectStyle(slide.subtitle_effect, slide.subtitle_start) }} data-testid="hero-subtitle-lg">
                  <div className="text-xl font-semibold [&_em]:italic" style={{ fontFamily: 'Playfair Display, serif', color: 'white' }} dangerouslySetInnerHTML={{ __html: normalizeRichText(tt(slide.subtitle)) }} />
                </div>
              )}
              {slide.description && (
                <div className="absolute max-w-[45%]" style={{ left: toLeftPct(slide.description_x, 100), top: toTopPct(slide.description_y, 120), ...effectStyle(slide.description_effect, slide.description_start) }} data-testid="hero-description-lg">
                  <div className="text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }} dangerouslySetInnerHTML={{ __html: normalizeRichText(tt(slide.description)) }} />
                </div>
              )}
              {(slide.button_text || slide.button_2_text || slide.button_3_text) && (
                <div className="absolute" style={{ left: toLeftPct(slide.button_x, 100), top: toTopPct(slide.button_y, 180), ...effectStyle(slide.button_effect, slide.button_start) }}>
                  <HeroButtonsRow slide={slide} dataTestId="hero-cta-btn-lg" />
                </div>
              )}
              {(slide.slide_type === 'video' && slide.video_embed) && (
                <div className="absolute" style={{ left: toLeftPct(slide.media_x, 400), top: toTopPct(slide.media_y, 50), width: slide.media_width ? `${slide.media_width}px` : '420px', ...effectStyle(slide.media_effect, slide.media_start) }}>
                  <VideoEmbed url={slide.video_embed} className="rounded-lg overflow-hidden shadow-2xl aspect-video" style={slide.media_height ? { height: `${slide.media_height}px`, aspectRatio: 'unset' } : {}} />
                </div>
              )}
              {(slide.slide_type === 'photo' && slide.photo) && (
                <div className="absolute" style={{ left: toLeftPct(slide.media_x, 400), top: toTopPct(slide.media_y, 50), width: slide.media_width ? `${slide.media_width}px` : '420px', ...effectStyle(slide.media_effect, slide.media_start) }}>
                  <img src={slide.photo} alt="" className="rounded-lg shadow-2xl w-full object-cover" style={slide.media_height ? { maxHeight: `${slide.media_height}px` } : { maxHeight: '400px' }} />
                </div>
              )}
            </div>

            {/* Mobile & Tablet (below lg): Stacked flow layout */}
            <div className="lg:hidden flex flex-col items-start gap-4">
              {(slide.slide_type === 'photo' && slide.photo) && (
                <div className="w-full max-w-sm mx-auto mb-2" style={effectStyle(slide.media_effect, slide.media_start)}>
                  <img src={slide.photo} alt="" className="rounded-lg shadow-2xl w-full object-cover max-h-[250px]" />
                </div>
              )}
              {(slide.slide_type === 'video' && slide.video_embed) && (
                <div className="w-full max-w-sm mx-auto mb-2" style={effectStyle(slide.media_effect, slide.media_start)}>
                  <VideoEmbed url={slide.video_embed} className="rounded-lg overflow-hidden shadow-2xl aspect-video" />
                </div>
              )}
              {slide.title && (
                <div style={effectStyle(slide.title_effect, slide.title_start)} data-testid="hero-title">
                  <div className="text-3xl sm:text-4xl font-bold leading-tight [&_em]:italic" style={{ fontFamily: 'Playfair Display, serif', color: 'white' }} dangerouslySetInnerHTML={{ __html: normalizeRichText(tt(slide.title)) }} />
                </div>
              )}
              {slide.subtitle && (
                <div style={effectStyle(slide.subtitle_effect, slide.subtitle_start)} data-testid="hero-subtitle">
                  <div className="text-base sm:text-lg font-semibold [&_em]:italic" style={{ fontFamily: 'Playfair Display, serif', color: 'white' }} dangerouslySetInnerHTML={{ __html: normalizeRichText(tt(slide.subtitle)) }} />
                </div>
              )}
              {slide.description && (
                <div style={effectStyle(slide.description_effect, slide.description_start)} data-testid="hero-description">
                  <div className="text-sm sm:text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }} dangerouslySetInnerHTML={{ __html: normalizeRichText(tt(slide.description)) }} />
                </div>
              )}
              {(slide.button_text || slide.button_2_text || slide.button_3_text) && (
                <div style={effectStyle(slide.button_effect, slide.button_start)} className="mt-2">
                  <HeroButtonsRow slide={slide} size="sm" />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Slide indicators */}
      {allSlides.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {allSlides.map((_, i) => (
            <button key={i} onClick={() => { setCurrentSlide(i); setAnimKey(p => p + 1); }}
              className={`w-2.5 h-2.5 rounded-full transition-all ${i === currentSlide ? 'bg-white scale-110' : 'bg-white/30 hover:bg-white/50'}`}
              data-testid={`hero-dot-${i}`} />
          ))}
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PcpHero — Private Community Pro hero (Litho-style "Editorial Dark", Option B
// approved 2026-08-21). Consumes the SAME slide fields as HeroSection
// (title/subtitle/description/up to 3 buttons/photo|video_embed/background) but
// lays them out in a fixed two-column editorial grid over the real hero photo +
// a navy legibility veil — instead of the CMS canvas x/y coordinates. Keeps
// data-testid="hero-section" so the transparent-navbar logic still fires.
// Only rendered when settings.active_theme === 'privatecommunity'.
const PCP_HERO_FONT = "'Sora','Space Grotesk',system-ui,sans-serif";

function PcpHeroButtons({ slide }) {
  const tt = useT();
  const { user } = useAuth();
  const loggedIn = !!user;
  // Same Action model as HeroButtonsRow (see lib/ctaActions).
  const buttons = [
    { text: slide.button_text,   url: tt(slide.button_url || slide.button_link), target: slide.window_open === 'new' ? '_blank' : '_self',          action: slide.button_action },
    { text: slide.button_2_text, url: tt(slide.button_2_url), target: slide.button_2_window_open === 'new' ? '_blank' : '_self', action: slide.button_2_action },
    { text: slide.button_3_text, url: tt(slide.button_3_url), target: slide.button_3_window_open === 'new' ? '_blank' : '_self', action: slide.button_3_action },
  ]
    .filter(b => tt(b.text))
    .map(b => { const r = resolveCta(b, { loggedIn }); return r.visible ? { ...b, href: r.href, target: r.target } : null; })
    .filter(Boolean);
  if (buttons.length === 0) return null;
  const [primary, ...rest] = buttons;
  return (
    <div className="flex items-center gap-4 flex-wrap" data-testid="hero-cta-row">
      {primary && (
        <a href={primary.href || '#'} target={primary.target} rel="noopener noreferrer" className="group relative inline-flex items-center flex-none rounded-full overflow-hidden" style={{ height: 66 }} data-testid="hero-cta-btn-0">
          {/* green ball → expands into the full pill background on hover */}
          <span className="absolute left-0 top-0 h-full w-[66px] group-hover:w-full rounded-full transition-[width] duration-500 ease-out" style={{ backgroundColor: 'var(--color-accent, #73af5f)', boxShadow: '0 10px 24px -12px rgba(0,0,0,.5)', zIndex: 0 }} />
          <span className="relative z-10 inline-flex items-center justify-center flex-none" style={{ width: 66, height: 66 }}>
            <ArrowRight className="w-5 h-5 transition-transform duration-500 group-hover:translate-x-0.5" style={{ color: 'var(--accent-ink)' }} />
          </span>
          <span className="relative z-10 font-semibold uppercase whitespace-nowrap text-white transition-colors duration-300 delay-100 group-hover:text-[color:var(--accent-ink)] group-hover:delay-150" style={{ fontSize: 13, letterSpacing: '.14em', paddingLeft: 4, paddingRight: 30 }}>{tt(primary.text)}</span>
        </a>
      )}
      {/* every secondary button (incl. "Notify Me!") renders as the same outlined
          pill — Option A: fills brand green on hover (hero always sits on a dark photo). */}
      {rest.map((b, i) => (
        <a key={i} href={b.href || '#'} target={b.target} rel="noopener noreferrer" className="pcpb pcpb--outline flex-none whitespace-nowrap" data-contrast="dark" style={{ padding: '14px 26px', fontSize: 13, letterSpacing: '.05em' }} data-testid={`hero-cta-btn-${i + 1}`}>{tt(b.text)}</a>
      ))}
    </div>
  );
}

export function PcpHero({ slides, data, titleSize }) {
  const tt = useT();
  const titleSz = heroTitleSize(titleSize);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const allSlides = useMemo(() => slides && slides.length > 0 ? slides : (data?.title ? [data] : []), [slides, data]);

  useEffect(() => {
    if (allSlides.length <= 1) return;
    const s = allSlides[currentSlide];
    const delay = s?.delay || 9400;
    const timer = setTimeout(() => {
      setCurrentSlide(prev => (prev + 1) % allSlides.length);
      setAnimKey(prev => prev + 1);
    }, delay);
    return () => clearTimeout(timer);
  }, [currentSlide, allSlides]);

  if (allSlides.length === 0) return null;
  const slide = allSlides[currentSlide];
  const bg = slide.background || slide.background_image || '';
  // Title is CMS rich text; render as HTML. Convert non-breaking spaces to
  // regular spaces so the oversized headline wraps into the stacked Litho look.
  const titleHTML = normalizeRichText(tt(slide.title) || '').replace(/&nbsp;|\u00A0/g, ' ');
  const subtitleHTML = normalizeRichText(tt(slide.subtitle) || '');
  const hasSubtitle = subtitleHTML.replace(/<[^>]*>/g, '').replace(/&nbsp;|\u00A0/g, ' ').trim().length > 0;
  const hasVideo = slide.slide_type === 'video' && slide.video_embed;
  const hasPhoto = slide.slide_type === 'photo' && slide.photo;
  const hasMedia = hasVideo || hasPhoto;

  const mediaEl = hasVideo
    ? <VideoEmbed url={slide.video_embed} className="w-full h-full" />
    : hasPhoto
    ? <img src={slide.photo} alt="" className="w-full h-full object-cover" />
    : null;

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section" style={{ fontFamily: PCP_HERO_FONT }}>
      <style>{HERO_KEYFRAMES}</style>
      {/* full-bleed hero background photo */}
      {bg && <div className="absolute inset-0 bg-cover bg-center transition-all duration-700" style={{ backgroundImage: `url(${bg})` }} />}
      {/* navy veil — dense on the left for the copy, opening up to the right so the photo + media read (Litho digital-agency style) */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(100deg, rgba(9,15,26,.94) 0%, rgba(12,20,34,.82) 30%, rgba(14,22,36,.5) 52%, rgba(9,15,24,.34) 100%)' }} />
      <div className="absolute inset-0" style={{ boxShadow: 'inset 0 -160px 150px -80px rgba(0,0,0,.55)' }} />
      {/* big brand accent circle bleeding off the top-right corner */}
      <div className="absolute pointer-events-none hidden md:block" style={{ right: '-7vw', top: '-11vw', width: '34vw', height: '34vw', maxWidth: 560, maxHeight: 560, borderRadius: '50%', background: 'radial-gradient(circle at 42% 42%, color-mix(in srgb, var(--color-accent) 50%, transparent), color-mix(in srgb, var(--color-accent) 14%, transparent) 58%, color-mix(in srgb, var(--color-accent) 0%, transparent) 72%)' }} />
      <svg className="absolute pointer-events-none hidden lg:block" style={{ right: '30%', top: '18%', opacity: .35 }} width="320" height="320" viewBox="0 0 320 320" fill="none"><circle cx="160" cy="160" r="118" stroke="rgba(255,255,255,.4)" strokeWidth="1.2"/><circle cx="160" cy="160" r="158" stroke="color-mix(in srgb, var(--color-accent) 35%, transparent)" strokeWidth="1"/></svg>
      <svg className="absolute pointer-events-none" style={{ left: '3vw', top: '24%', transform: 'rotate(-16deg)', opacity: .85 }} width="64" height="42" viewBox="0 0 64 42" fill="none"><path d="M2 37 C 18 -4, 55 -2, 62 5 C 40 11, 18 24, 2 37 Z" fill="var(--accent-strong)"/></svg>

      {/* desktop media — large, right-aligned with a margin from the edge + green offset panel */}
      {hasMedia && (
        <div className="absolute inset-y-0 right-0 hidden lg:flex items-center z-10 pointer-events-none" style={{ paddingRight: 'clamp(32px, 4.5vw, 96px)' }}>
          <div className="relative pointer-events-auto" style={{ width: 'min(44vw, 720px)', height: 'min(62vh, 540px)' }}>
            {/* subtle green accent edge peeking behind (bottom-right, thin) */}
            <div className="absolute" style={{ left: 16, top: 18, right: -7, bottom: -7, borderRadius: 24, background: 'var(--color-accent, #73af5f)' }} />
            {/* framed media */}
            <div className="absolute inset-0 overflow-hidden" style={{ borderRadius: 22, boxShadow: '0 45px 110px -45px rgba(0,0,0,.7)', border: '1px solid color-mix(in srgb, var(--color-accent) 28%, transparent)' }}>
              {mediaEl}
            </div>
          </div>
        </div>
      )}

      {/* copy — full-bleed left column with viewport-scaled padding (no centered max-w container) */}
      <div key={animKey} className="relative z-20 w-full flex items-center pointer-events-none">
        <div className="pointer-events-auto py-28 max-w-[680px] lg:max-w-[min(1000px,52vw)]" style={{ paddingLeft: 'clamp(24px, 7vw, 140px)', paddingRight: 24 }}>
          {hasMedia && (
            <div className="lg:hidden mb-7 rounded-2xl overflow-hidden shadow-2xl" style={{ border: '1px solid color-mix(in srgb, var(--color-accent) 25%, transparent)' }}>
              {hasVideo ? <div className="aspect-video">{mediaEl}</div> : <img src={slide.photo} alt="" className="w-full object-cover max-h-[250px]" />}
            </div>
          )}
          {hasSubtitle && (
            <div className="inline-flex items-center gap-2 rounded-full mb-7" style={{ background: 'color-mix(in srgb, var(--color-accent) 14%, transparent)', border: '1px solid color-mix(in srgb, var(--color-accent) 40%, transparent)', color: 'var(--accent-soft)', padding: '7px 14px', fontSize: 11.5, fontWeight: 600, letterSpacing: '.18em', textTransform: 'uppercase' }} data-testid="hero-subtitle">
              <span style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--color-accent, #73af5f)', flex: '0 0 auto' }} />
              <span dangerouslySetInnerHTML={{ __html: subtitleHTML }} />
            </div>
          )}
          <h1 className="lg:max-w-[640px] [&_p]:m-0 [&_em]:italic" style={{ fontFamily: PCP_HERO_FONT, fontWeight: 800, letterSpacing: '-.04em', color: '#fff', fontSize: titleSz.fontSize, lineHeight: titleSz.lineHeight, textShadow: '0 2px 30px rgba(0,0,0,.35)', overflowWrap: 'normal', wordBreak: 'keep-all' }} data-testid="hero-title" dangerouslySetInnerHTML={{ __html: titleHTML }} />
          {tt(slide.description) && (
            <div className="mt-7 max-w-md lg:max-w-[min(576px,42vw)] text-base md:text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,.74)' }} data-testid="hero-description" dangerouslySetInnerHTML={{ __html: normalizeRichText(tt(slide.description)) }} />
          )}
          <div className="mt-10"><PcpHeroButtons slide={slide} /></div>
        </div>
      </div>

      {/* scroll cue */}
      <div className="absolute hidden md:flex items-center gap-3" style={{ left: 40, bottom: 34 }}>
        <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: 11, letterSpacing: '.32em', textTransform: 'uppercase', color: 'rgba(255,255,255,.55)' }}>Scroll</span>
        <span style={{ width: 1, height: 46, background: 'rgba(255,255,255,.4)' }} />
      </div>

      {/* slide indicators */}
      {allSlides.length > 1 && (
        <div className="absolute bottom-7 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {allSlides.map((_, i) => (
            <button key={i} onClick={() => { setCurrentSlide(i); setAnimKey(p => p + 1); }} aria-label={`Slide ${i + 1}`}
              className="rounded-full transition-all"
              style={{ width: i === currentSlide ? 26 : 8, height: 8, background: i === currentSlide ? 'var(--color-accent, #73af5f)' : 'rgba(255,255,255,.35)' }}
              data-testid={`hero-dot-${i}`} />
          ))}
        </div>
      )}
    </section>
  );
}
